import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useStore } from '../../../../hooks/useStore';
import { CardOnboardingResponse, useCardOnboarding } from '../../../../queries/pxtraCard';
import { FeedbackAlert } from '../../atomic/feedback/alert';
import { isOnboardingOpen, onboardingSentence, onboardingTitleStatus } from './sentence';

export const ONBOARDING_PATH = '/benefits/pxtra-card/settings';

export const onboardingSeverity = (
  onboarding: CardOnboardingResponse
): 'error' | 'warning' | 'info' => {
  if (onboarding.phase === 'refused') return 'error';

  return onboarding.action_required_by_company ? 'warning' : 'info';
};

type ViewProps = {
  onboarding: CardOnboardingResponse;
  onOpen: () => void;
};

/**
 * Wherever card functions are configured or expected, the current stand in one
 * sentence with a link into the setup — never a bare "not available yet".
 */
export const CardOnboardingNoticeView: FC<ViewProps> = ({ onboarding, onOpen }) => {
  const { t } = useTranslation();

  if (!isOnboardingOpen(onboarding)) return null;

  const sentence = onboardingSentence(onboarding);
  const status = onboardingTitleStatus(onboarding);

  return (
    // FeedbackAlert sets `marginLeft: 12px` on the action slot, which drops
    // MUI's own `auto` and leaves the link against the text. The selector has
    // to out-specify that rule, not just repeat it.
    <Box className="mb-4" sx={{ '& .MuiAlert-root > .MuiAlert-action': { marginLeft: 'auto' } }}>
      <FeedbackAlert
        severity={onboardingSeverity(onboarding)}
        bulletPoints={false}
        title={t('pxtraCard:onboardingNoticeTitle', { status: t(status.key, status.params ?? {}) })}
        description={t(sentence.key, {
          requirement: sentence.requirement
            ? t(`pxtraCard:requirement_${sentence.requirement}`)
            : ''
        })}
        actionText={t('pxtraCard:onboardingNoticeAction')}
        actionCallback={onOpen}
      />
    </Box>
  );
};

/** The connected notice the pages mount. */
export const CardOnboardingNotice = observer(() => {
  const navigate = useNavigate();
  const { companyStore } = useStore();
  const companyId = companyStore.company?.id ?? '';

  const { data: onboarding } = useCardOnboarding(companyId);

  if (!onboarding) return null;

  return (
    <CardOnboardingNoticeView onboarding={onboarding} onOpen={() => navigate(ONBOARDING_PATH)} />
  );
});
